import { useState } from "react";
import UiIcon from "../UiIcon";
import { groupTimeline } from "./contributionWorkflow";

const ICON = {
    created: "edit", submitted: "send", resubmitted: "send", updated: "edit", revision_requested: "warning",
    approved: "check", rejected: "close", attachment_added: "paperclip", attachment_removed: "paperclip", other: "edit",
};
const TONE = {
    created: "neutral", submitted: "info", resubmitted: "info", revision_requested: "warning", approved: "success", rejected: "danger",
};

export function formatDateTime(value, language) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(language === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
}

export function StatusPill({ status, c }) {
    return <span className={`cvi-contrib-status is-${status || "unknown"}`}><i aria-hidden="true" />{c.status[status] || status || "—"}</span>;
}

function Actor({ event, c }) {
    if (!event.actorName) return <span className="cvi-contrib-tl-actor is-unknown">{c.timeline.byUnknown}</span>;
    return <span className="cvi-contrib-tl-actor">{c.timeline.by(event.actorName)}{event.isYou ? ` (${c.common.you})` : ""}</span>;
}

function EventRow({ event, c, language, extra }) {
    const when = formatDateTime(event.at, language);
    return <li className={`cvi-contrib-tl-item is-done is-${TONE[event.kind] || "neutral"}`}>
        <span className="cvi-contrib-tl-node" aria-hidden="true"><UiIcon name={ICON[event.kind] || "edit"} size={13} /></span>
        <div className="cvi-contrib-tl-body">
            <div className="cvi-contrib-tl-head">
                <strong>{c.timeline.events[event.kind] || c.timeline.events.other}</strong>
                {when ? <time dateTime={event.at}>{when}</time> : <span className="cvi-contrib-muted">{c.common.notRecorded}</span>}
            </div>
            <Actor event={event} c={c} />
            {extra}
            {event.note && <blockquote className="cvi-contrib-tl-note">{event.note}</blockquote>}
        </div>
    </li>;
}

function MinorGroup({ events, c, language }) {
    const [open, setOpen] = useState(false);
    return <li className="cvi-contrib-tl-item is-minor">
        <span className="cvi-contrib-tl-node" aria-hidden="true" />
        <div className="cvi-contrib-tl-body">
            <button type="button" className="cvi-contrib-tl-toggle" aria-expanded={open} onClick={() => setOpen(v => !v)}>
                {c.timeline.minor(events.length)}<UiIcon name="chevronDown" size={13} />
            </button>
            {open && <ul className="cvi-contrib-tl-minor">{events.map(event => <li key={event.id}>
                <span>{c.timeline.events[event.kind] || c.timeline.events.other}{event.note ? ` · ${event.note}` : ""}</span>
                <small>{[event.actorName ? c.timeline.by(event.actorName) : c.timeline.byUnknown, formatDateTime(event.at, language)].filter(Boolean).join(" · ")}</small>
            </li>)}</ul>}
        </div>
    </li>;
}

function PendingRow({ pending, c }) {
    const p = c.timeline.pending;
    const title = { submit: p.submit, review: p.review, revise: p.revise }[pending.kind];
    const who = pending.waitingOn && pending.waitingOnYou ? `${pending.waitingOn} (${c.common.you})` : pending.waitingOn;
    const help = pending.kind === "submit" ? p.submitHelp : pending.kind === "review" ? p.reviewHelp(who) : p.reviseHelp(who);
    return <li className="cvi-contrib-tl-item is-pending" aria-current="step">
        <span className="cvi-contrib-tl-node" aria-hidden="true"><UiIcon name="schedule" size={13} /></span>
        <div className="cvi-contrib-tl-body">
            <div className="cvi-contrib-tl-head"><strong>{title}</strong></div>
            <span className="cvi-contrib-tl-actor">{help}</span>
        </div>
    </li>;
}

// Chronological approval history with the current status and the next pending step.
export default function ContributionTimeline({ workflow, c, language, impact = null, headingLevel = 2 }) {
    const H = `h${headingLevel}`;
    const rows = groupTimeline(workflow.events);
    const decisionId = workflow.decision?.id;
    return <section className="cvi-contrib-card cvi-contrib-timeline" aria-labelledby="cvi-contrib-timeline-title">
        <header className="cvi-contrib-card-head">
            <div><H id="cvi-contrib-timeline-title">{c.timeline.title}</H><p>{c.timeline.subtitle}</p></div>
            <div className="cvi-contrib-current"><span>{c.timeline.current}</span><StatusPill status={workflow.status} c={c} /></div>
        </header>
        {rows.length === 0 && !workflow.pending ? <p className="cvi-contrib-empty">{c.timeline.empty}</p> : <ol className="cvi-contrib-tl">
            {rows.map((row, index) => row.type === "minor"
                ? <MinorGroup key={`minor-${index}`} events={row.events} c={c} language={language} />
                : <EventRow key={row.event.id} event={row.event} c={c} language={language}
                    extra={row.event.id === decisionId && workflow.status === "approved" && impact?.label
                        ? <span className="cvi-contrib-tl-extra">{c.timeline.impactAwarded(impact.label, impact.points)}</span> : null} />)}
            {workflow.pending && <PendingRow pending={workflow.pending} c={c} />}
            {workflow.final && <li className={`cvi-contrib-tl-item is-outcome is-${workflow.status}`}>
                <span className="cvi-contrib-tl-node" aria-hidden="true"><UiIcon name="flag" size={13} /></span>
                <div className="cvi-contrib-tl-body"><strong>{c.timeline.outcome[workflow.status]}</strong></div>
            </li>}
        </ol>}
        {(workflow.derived || workflow.missingActors) && <p className="cvi-contrib-limitation" role="note">
            <UiIcon name="help" size={14} />{workflow.derived ? c.timeline.limitation.derived : c.timeline.limitation.actor}
        </p>}
    </section>;
}
